import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceIcon from "@mui/icons-material/Place";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate, useParams } from "react-router-dom";
import { getGame, registerForGame } from "../../services/api/games";
import { hasLocallyValidSession, fetchCurrentUserProfile } from "../../services/auth/auth-session";
import LoginDialog from "../auth/login-dialog";
import { formatGameDate, formatGameTime, formatCost } from "../../components/GameCard";
import { loadErrorMessage } from "../../utils/api-error";

export default function GameSignupView() {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [game, setGame] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [role, setRole] = React.useState("SKATER");
  const [preferredPosition, setPreferredPosition] = React.useState("");

  const [submitState, setSubmitState] = React.useState({ loading: false, error: "", success: false });
  const [loginOpen, setLoginOpen] = React.useState(false);
  // Signing up needs a login, and you sign up as yourself, so the account email is fixed.
  const [emailLocked, setEmailLocked] = React.useState(false);

  const loadProfile = React.useCallback(() => {
    return fetchCurrentUserProfile()
      .then((profile) => {
        if (!profile?.email) return;
        setEmail(profile.email);
        setFirstName((prev) => prev || profile.firstName || "");
        setLastName((prev) => prev || profile.lastName || "");
        setEmailLocked(true);
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (hasLocallyValidSession()) loadProfile();
  }, [loadProfile]);

  React.useEffect(() => {
    if (!gameId) return;
    getGame(gameId)
      .then(setGame)
      .catch((err) => setLoadError(loadErrorMessage(err, "Failed to load game.")))
      .finally(() => setLoading(false));
  }, [gameId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasLocallyValidSession()) {
      setLoginOpen(true);
      return;
    }
    setSubmitState({ loading: true, error: "", success: false });
    try {
      await registerForGame(gameId, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        preferredPosition: role === "SKATER" && preferredPosition ? preferredPosition : undefined,
      });
      setSubmitState({ loading: false, error: "", success: true });
    } catch (err) {
      setSubmitState({
        loading: false,
        error: err instanceof Error ? err.message : "Registration failed. Please try again.",
        success: false,
      });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  if (loadError || !game) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{loadError || "Game not found."}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/search")}>
          Back to search
        </Button>
      </Container>
    );
  }

  const cityState =
    game.league?.city && game.league?.state
      ? `${game.league.city}, ${game.league.state}`
      : "";
  const locationAddress = game.rink?.address;
  const maxSkater = game.maxSkaterSpots ?? 0;
  const maxGoalie = game.maxGoalieSpots ?? 0;
  const skatersFull = maxSkater > 0 && (game.skaterSpotsFilled ?? 0) >= maxSkater;
  const goaliesFull = maxGoalie > 0 && (game.goalieSpotsFilled ?? 0) >= maxGoalie;

  if (submitState.success) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={3} alignItems="center" sx={{ textAlign: "center" }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
          <Typography variant="h4" component="h3">
            You&apos;re on the ice!
          </Typography>
          <Typography color="text.secondary">
            {firstName}, you&apos;re signed up as a{" "}
            <strong>{role === "GOALIE" ? "goalie" : "skater"}</strong> for{" "}
            {game.arenaName || game.league?.name} on {formatGameDate(game.startsAt)} at{" "}
            {formatGameTime(game.startsAt)}.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => navigate(`/games/${gameId}/roster`)}>
              View Roster
            </Button>
            <Button variant="outlined" onClick={() => navigate("/search")}>
              Find More Games
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={() => {
          setLoginOpen(false);
          loadProfile();
        }}
      />
      <Stack spacing={3}>
        <Button
          variant="text"
          onClick={() => navigate(`/games/${gameId}/roster`)}
          sx={{ alignSelf: "flex-start", pl: 0 }}
        >
          ← View Roster
        </Button>

        <Box>
          <Typography variant="h4" component="h3" gutterBottom>
            Sign Up
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {game.arenaName || game.league?.name}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarMonthIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatGameDate(game.startsAt)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatGameTime(game.startsAt)}
                {game.durationMinutes ? ` · ${game.durationMinutes} min` : ""}
              </Typography>
            </Stack>
            {(locationAddress || cityState) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <PlaceIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {locationAddress
                    ? `${locationAddress}${cityState ? ` · ${cityState}` : ""}`
                    : cityState}
                </Typography>
              </Stack>
            )}
          </Stack>
          {game.league?.playerCost > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Cost:{" "}
              <strong>
                {role === "GOALIE"
                  ? formatCost(game.league.goalieCost)
                  : formatCost(game.league.playerCost)}
              </strong>{" "}
              per player. Payment details will be provided by the organizer.
            </Alert>
          )}
        </Box>

        <Divider />

        <Card variant="outlined">
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Typography variant="h6">Your Info</Typography>

                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={emailLocked}
                  helperText={emailLocked ? "This is the email on your account." : "Sign in to sign up for this game."}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="First name"
                    required
                    fullWidth
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <TextField
                    label="Last name"
                    required
                    fullWidth
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Stack>

                <FormControl required>
                  <FormLabel>I&apos;m signing up as</FormLabel>
                  <RadioGroup
                    row
                    value={role}
                    onChange={(e) => {
                      setRole(e.target.value);
                      setPreferredPosition("");
                    }}
                  >
                    <FormControlLabel
                      value="SKATER"
                      control={<Radio />}
                      label="Skater"
                      disabled={skatersFull}
                    />
                    <FormControlLabel
                      value="GOALIE"
                      control={<Radio />}
                      label="Goalie"
                      disabled={goaliesFull}
                    />
                  </RadioGroup>
                  {skatersFull && role === "SKATER" && (
                    <Typography variant="caption" color="error">
                      Skater spots are full.
                    </Typography>
                  )}
                  {goaliesFull && (
                    <Typography variant="caption" color="text.secondary">
                      Goalie spots are full.
                    </Typography>
                  )}
                </FormControl>

                {role === "SKATER" && (
                  <TextField
                    select
                    label="Preferred position (optional)"
                    value={preferredPosition}
                    onChange={(e) => setPreferredPosition(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>No preference</em>
                    </MenuItem>
                    <MenuItem value="CENTER">Center</MenuItem>
                    <MenuItem value="WING">Wing</MenuItem>
                    <MenuItem value="DEFENSE">Defense</MenuItem>
                  </TextField>
                )}

                {submitState.error && (
                  <Alert severity="error" onClose={() => setSubmitState((s) => ({ ...s, error: "" }))}>
                    {submitState.error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={submitState.loading || (role === "SKATER" && skatersFull) || (role === "GOALIE" && goaliesFull)}
                >
                  {submitState.loading ? "Signing up…" : "Confirm Sign Up"}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
